import { el } from '../utils/dom.js';
import { importPSD } from '../io/PSDImporter.js';

/**
 * PSDImportDialog — Educational dialog + progress UI for PSD → tileset conversion.
 *
 * Shown when a user tries to load a .psd file via "Add Tileset...".
 * Explains what will happen, lets them proceed or cancel, shows progress,
 * and loads the resulting tileset into the editor.
 */
export class PSDImportDialog {
  constructor(state, tilesetLoader, compositor) {
    this.state = state;
    this.tilesetLoader = tilesetLoader;
    this.compositor = compositor;
  }

  /**
   * Show the import dialog for a PSD file.
   * @param {File} file — The .psd file from the file picker
   * @param {function} [onSuccess] — Called with the loaded tileset name
   */
  show(file, onSuccess) {
    const overlay = el('div', { className: 'dialog-overlay' });
    const dialog = el('div', { className: 'dialog', style: { minWidth: '440px', maxWidth: '500px' } });

    // Header
    dialog.appendChild(el('h2', { className: 'dialog-title' }, 'Import PSD as Tileset'));

    // Educational message
    const info = el('div', { className: 'psd-import-info' });
    info.appendChild(el('p', {},
      'PSD files contain layered artwork that needs to be converted into a grid-aligned tileset before TileForge can use it.'));
    info.appendChild(el('p', {},
      'TileForge will extract each layer from the PSD and pack them into a tileset grid automatically. This may take a moment for large files.'));

    const details = el('div', { className: 'psd-import-details' });
    details.appendChild(el('div', { className: 'psd-import-detail' },
      el('strong', {}, 'File: '), file.name));
    details.appendChild(el('div', { className: 'psd-import-detail' },
      el('strong', {}, 'Size: '), `${(file.size / 1024 / 1024).toFixed(1)} MB`));
    info.appendChild(details);

    // Learn more link
    const learnMore = el('a', {
      href: 'help/psd-import.html',
      target: '_blank',
      className: 'psd-import-learn-more',
    });
    learnMore.textContent = 'Learn more about PSD files and tilesets';
    info.appendChild(learnMore);

    dialog.appendChild(info);

    // Tile size option
    const sizeGroup = el('div', { className: 'form-group', style: { marginTop: '12px' } });
    sizeGroup.appendChild(el('label', { className: 'form-label' }, 'Tile Size'));
    const sizeRow = el('div', { className: 'form-row' });
    const sizeSelect = el('select', { className: 'form-input', style: { flex: '1' } });
    sizeSelect.appendChild(el('option', { value: 'auto' }, 'Auto-detect'));
    for (const size of [16, 32, 48, 64, 96, 128]) {
      sizeSelect.appendChild(el('option', { value: String(size) }, `${size} \u00D7 ${size}`));
    }
    sizeRow.appendChild(sizeSelect);
    sizeGroup.appendChild(sizeRow);
    dialog.appendChild(sizeGroup);

    // Progress area (hidden initially)
    const progressArea = el('div', { className: 'psd-import-progress', style: { display: 'none' } });
    const progressBar = el('div', { className: 'psd-import-progress-bar' });
    const progressFill = el('div', { className: 'psd-import-progress-fill' });
    progressBar.appendChild(progressFill);
    progressArea.appendChild(progressBar);
    const progressText = el('div', { className: 'psd-import-progress-text' }, 'Preparing...');
    progressArea.appendChild(progressText);
    dialog.appendChild(progressArea);

    // Error area
    const errorArea = el('div', { className: 'psd-import-error' });
    dialog.appendChild(errorArea);

    // Buttons
    const btnRow = el('div', { className: 'dialog-buttons' });
    const importBtn = el('button', { className: 'btn btn-primary', onClick: () => {
      this._doImport(file, sizeSelect.value, overlay, {
        progressArea, progressFill, progressText, errorArea, importBtn, cancelBtn,
      }, onSuccess);
    }}, 'Convert & Import');
    const cancelBtn = el('button', { className: 'btn', onClick: () => overlay.remove() }, 'Cancel');
    btnRow.append(importBtn, cancelBtn);
    dialog.appendChild(btnRow);

    overlay.appendChild(dialog);
    document.body.appendChild(overlay);
  }

  async _doImport(file, sizeValue, overlay, ui, onSuccess) {
    const { progressArea, progressFill, progressText, errorArea, importBtn, cancelBtn } = ui;

    // Switch to progress mode
    importBtn.disabled = true;
    importBtn.textContent = 'Converting...';
    cancelBtn.disabled = true;
    progressArea.style.display = '';
    errorArea.textContent = '';

    try {
      const buffer = await file.arrayBuffer();

      const options = {
        onProgress: (pct, msg) => {
          progressFill.style.width = `${pct}%`;
          progressText.textContent = msg;
        },
      };

      if (sizeValue !== 'auto') {
        const size = parseInt(sizeValue, 10);
        options.tileWidth = size;
        options.tileHeight = size;
      }

      const result = await importPSD(buffer, options);

      if (result.tileCount === 0) {
        throw new Error('No visible tiles could be extracted from this PSD. It may not contain tileset artwork, or the layers may use unsupported features.');
      }

      // Determine tile dimensions
      const tileWidth = result.canvas.width / result.columns;
      const tileHeight = result.canvas.height / result.rows;

      // Show preview instead of auto-loading — let user confirm
      progressArea.style.display = 'none';
      importBtn.style.display = 'none';
      cancelBtn.disabled = false;
      cancelBtn.textContent = 'Cancel';

      const previewArea = el('div', { className: 'psd-import-preview' });

      // Summary
      const summary = el('div', { className: 'psd-import-summary' },
        `Extracted ${result.tileCount} tiles at ${tileWidth}\u00D7${tileHeight}px`
        + (result.skipped > 0 ? ` (${result.skipped} layers skipped)` : ''));
      previewArea.appendChild(summary);

      // Warning for suspiciously few tiles
      if (result.tileCount < 5) {
        const warning = el('div', { className: 'psd-import-warning' },
          'This PSD produced very few tiles. It may not be a tileset \u2014 it could be a composite image or scene file. ' +
          'Tilesets typically have dozens or hundreds of tiles arranged in a grid.');
        previewArea.appendChild(warning);
      }

      // Canvas preview
      const previewCanvas = el('canvas', {
        className: 'psd-import-preview-canvas',
        style: { imageRendering: 'pixelated' },
      });
      const maxPreviewWidth = 400;
      const scale = Math.min(maxPreviewWidth / result.canvas.width, 2);
      previewCanvas.width = result.canvas.width * scale;
      previewCanvas.height = result.canvas.height * scale;
      const pCtx = previewCanvas.getContext('2d');
      pCtx.imageSmoothingEnabled = false;
      // Checkerboard background for transparency
      for (let y = 0; y < previewCanvas.height; y += 8) {
        for (let x = 0; x < previewCanvas.width; x += 8) {
          pCtx.fillStyle = (x + y) % 16 === 0 ? '#2a2a2a' : '#1a1a1a';
          pCtx.fillRect(x, y, 8, 8);
        }
      }
      pCtx.drawImage(result.canvas, 0, 0, previewCanvas.width, previewCanvas.height);
      previewArea.appendChild(previewCanvas);

      // Replace dialog content after error area
      errorArea.before(previewArea);

      // Add confirm button
      const confirmBtn = el('button', { className: 'btn btn-primary', onClick: () => {
        this._loadResult(result, file, overlay, onSuccess);
      }}, 'Use This Tileset');
      cancelBtn.before(confirmBtn);

    } catch (err) {
      console.error('PSD import error:', err);
      errorArea.textContent = err.message || 'Failed to import PSD file.';
      importBtn.disabled = false;
      importBtn.textContent = 'Retry';
      cancelBtn.disabled = false;
      progressArea.style.display = 'none';
    }
  }

  async _loadResult(result, file, overlay, onSuccess) {
    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = () => reject(new Error('Failed to create tileset image'));
      img.src = result.canvas.toDataURL('image/png');
    });

    const tileWidth = result.canvas.width / result.columns;
    const tileHeight = result.canvas.height / result.rows;
    const name = file.name.replace(/\.psd$/i, '');

    const imageKey = `psd:${name}`;
    const tilesets = this.state.get('tilesets');
    tilesets.push({
      name,
      image: imageKey,
      tileWidth,
      tileHeight,
      columns: result.columns,
      tileCount: result.tileCount,
      firstGid: tilesets.reduce((max, ts) => Math.max(max, (ts.firstGid || 0) + (ts.tileCount || 0)), 1),
      imageWidth: result.canvas.width,
      imageHeight: result.canvas.height,
      margin: 0,
      spacing: 0,
    });

    const tilesetImages = this.state.get('tilesetImages');
    tilesetImages.set(imageKey, img);
    this.state._notify('tilesets', tilesets, tilesets);
    this.state._notify('tilesetImages', tilesetImages, tilesetImages);

    this.compositor.requestRender();
    overlay.remove();
    if (onSuccess) onSuccess(name);
  }
}
